import type { ReactNode } from "react";
import { Link, type Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import {
  activationCameraKey,
  chronological,
  formatElapsed,
  type ActivationDetail,
  type ActivationOperatorNote,
  type ActivationSignal,
} from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant, formatText } from "../format";

interface ActivationDetailViewProps {
  readonly activation: ActivationDetail;
  /** Shown as a Tenant field, for a view reached from outside any one tenant. */
  readonly tenant?: { readonly label: string; readonly href?: Href };
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  /** Omit where the camera page cannot be reached; the camera then shows unlinked. */
  readonly cameraHref?: (siteId: string, cameraKey: string) => Href;
  /** Omit where there is no tenant list to narrow; site and camera then show unlinked. */
  readonly listHref?: (filters: { site: string; camera?: string }) => Href;
}

export function ActivationDetailView({
  activation,
  tenant,
  siteLabel,
  cameraLabel,
  cameraHref,
  listHref,
}: ActivationDetailViewProps) {
  const cameraKey = activationCameraKey(activation);
  const site = activation.a3sSite;

  return (
    <View gap="4xl">
      <Section title="Summary">
        <Field label="Ticket">{activation.ticketNo}</Field>
        <Field label="Description">{formatText(activation.description)}</Field>
        <Field label="Pending state">{formatText(activation.pendingState)}</Field>
        <Field label="Completed status">{formatText(activation.completedStatus)}</Field>
        <Field label="Reason">{formatText(activation.reason)}</Field>
        <Field label="Priority">{activation.priority !== undefined ? String(activation.priority) : EMPTY}</Field>
        {tenant !== undefined && (
          <Field label="Tenant">
            {tenant.href !== undefined ?
              <InlineLink href={tenant.href}>{tenant.label}</InlineLink>
            : tenant.label}
          </Field>
        )}
        <Field label="Site">
          {site === undefined ?
            EMPTY
          : listHref !== undefined ?
            <InlineLink href={listHref({ site })}>{siteLabel(site)}</InlineLink>
          : siteLabel(site)}
        </Field>
        <Field label="Camera">
          {site !== undefined && cameraKey !== undefined ?
            <View flexDirection="row" gap="lg" flexWrap="wrap">
              {cameraHref !== undefined ?
                <InlineLink href={cameraHref(site, cameraKey)}>{cameraLabel(cameraKey)}</InlineLink>
              : <Text textStyle="sm/regular" color="text" selectable>
                  {cameraLabel(cameraKey)}
                </Text>
              }
              {listHref !== undefined && (
                <InlineLink href={listHref({ site, camera: cameraKey })}>Other activations for this camera</InlineLink>
              )}
            </View>
          : EMPTY}
        </Field>
        {cameraKey !== undefined && <Field label="Camera key">{cameraKey}</Field>}
      </Section>

      <Section title="Timeline">
        <Field label="Created">{formatInstant(activation.createdDateTime)}</Field>
        <Field label="Responded">
          <TimelineValue
            from={activation.createdDateTime}
            at={activation.respondedDateTime}
            sinceLabel="after created"
          />
        </Field>
        <Field label="Completed">
          <TimelineValue
            from={activation.respondedDateTime ?? activation.createdDateTime}
            at={activation.completedDateTime}
            sinceLabel={activation.respondedDateTime !== undefined ? "after responded" : "after created"}
          />
        </Field>
      </Section>

      <Section title={`Signals (${String(activation.signals.length)})`}>
        {activation.signals.length === 0 ?
          <Empty>No a3s signals on this activation.</Empty>
        : chronological(activation.signals, (s) => s.receivedDateTime).map((signal, index) => (
            <SignalRow key={signal.a3sSignalId} signal={signal} isFirst={index === 0} />
          ))
        }
      </Section>

      <Section title={`Operator notes (${String(activation.operatorNotes.length)})`}>
        {activation.operatorNotes.length === 0 ?
          <Empty>No operator notes on this activation.</Empty>
        : chronological(activation.operatorNotes, (n) => n.noteDateTime).map((note, index) => (
            <OperatorNoteRow key={note.noteId} note={note} isFirst={index === 0} />
          ))
        }
      </Section>
    </View>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <View style={styles.section}>
      <Text textStyle="md/semibold" color="textMuted">
        {title}
      </Text>
      <View gap="md">{children}</View>
    </View>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <View style={styles.field}>
      <Text textStyle="xs/regular" color="textMuted" style={styles.fieldLabel}>
        {label}
      </Text>
      <View style={styles.fieldValue}>
        {typeof children === "string" ?
          <Text textStyle="sm/regular" color="text" selectable>
            {children}
          </Text>
        : children}
      </View>
    </View>
  );
}

function InlineLink({ href, children }: { href: Href; children: string }) {
  return (
    <Link href={href}>
      <Text textStyle="sm/regular" style={styles.link}>
        {children}
      </Text>
    </Link>
  );
}

function TimelineValue({ from, at, sinceLabel }: { from: string; at: string | undefined; sinceLabel: string }) {
  if (at === undefined) {
    return (
      <Text textStyle="sm/regular" color="text">
        {EMPTY}
      </Text>
    );
  }
  const elapsed = formatElapsed(from, at);
  return (
    <Text textStyle="sm/regular" color="text" selectable>
      {formatInstant(at)}
      {elapsed !== undefined && (
        <Text textStyle="xs/regular" color="textMuted">
          {`  ${elapsed} ${sinceLabel}`}
        </Text>
      )}
    </Text>
  );
}

function SignalRow({ signal, isFirst }: { signal: ActivationSignal; isFirst: boolean }) {
  return (
    <View style={[styles.entry, !isFirst && styles.entryDivider]}>
      <View flexDirection="row" gap="xl" flexWrap="wrap" alignItems="baseline">
        <Text textStyle="xs/regular" color="textMuted">
          {formatInstant(signal.receivedDateTime)}
        </Text>
        <Text textStyle="sm/regular" color="text">
          {formatText(signal.description)}
        </Text>
      </View>
      <Text textStyle="xs/regular" color="textMuted" selectable>
        a3s signal {signal.a3sSignalId}
      </Text>
    </View>
  );
}

function OperatorNoteRow({ note, isFirst }: { note: ActivationOperatorNote; isFirst: boolean }) {
  return (
    <View style={[styles.entry, !isFirst && styles.entryDivider]}>
      <Text textStyle="xs/regular" color="textMuted">
        {formatInstant(note.noteDateTime)}
      </Text>
      {/* Free text written by a monitoring operator; line breaks are theirs. */}
      <Text textStyle="sm/regular" color="text" selectable>
        {formatText(note.notes)}
      </Text>
    </View>
  );
}

function Empty({ children }: { children: string }) {
  return (
    <Text textStyle="sm/regular" color="textMuted">
      {children}
    </Text>
  );
}

const styles = StyleSheet.create((theme) => ({
  section: {
    backgroundColor: theme.colors.surfaceCardInner,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    padding: theme.spacing["4xl"],
    gap: theme.spacing["2xl"],
  },
  field: { flexDirection: "row", alignItems: "baseline", gap: theme.spacing.xl },
  fieldLabel: { width: 110 },
  fieldValue: { flex: 1 },
  link: { color: theme.colors.interactive.buttons.secondary_foreground, textDecorationLine: "underline" },
  entry: { gap: theme.spacing.xs, paddingTop: theme.spacing.md },
  entryDivider: { borderTopWidth: 1, borderColor: theme.colors.borderCard },
}));
